import React, { useState, useEffect, useCallback } from 'react';
import moment from 'moment';
import 'moment/locale/th';
import shrinkImage from './shrinkImage';

// หน้าคนซักผ้า (cleaners.role = 'laundry') -- เปิดจากลิงก์ในไลน์ (?t=...&view=laundry&batch=...)
// ขั้นตอน: แจ้งรับผ้า -> [รับทราบ] -> [รับผ้าแล้ว + จำนวนจริง] -> [ส่งคืนแล้ว + ยอดเงิน + รูปบิล] -> แอดมินโอน
const STATUS = {
  open: { label: 'ผ้าค้าง (ยังไม่ถึงรอบ)', color: '#8A938B' },
  notified: { label: 'รอมารับผ้า', color: '#B26B0E' },
  acknowledged: { label: 'รับทราบแล้ว', color: '#2C6DAF' },
  picked_up: { label: 'อยู่ที่ร้านซัก', color: '#2C6DAF' },
  returned: { label: 'รอแอดมินตรวจ/โอน', color: '#B26B0E' },
  approved: { label: 'อนุมัติแล้ว รอโอน', color: '#0F8A56' },
  paid: { label: 'จ่ายแล้ว', color: '#0F8A56' },
};
const statusOf = (b) => (b.payment_status === 'void' && ['returned', 'approved'].includes(b.status)
  ? { label: 'ส่งคืนแล้ว', color: '#0F8A56' }
  : (STATUS[b.status] || { label: b.status, color: '#8A938B' }));

const ACTIVE = ['open', 'notified', 'acknowledged'];
const SOURCE = { checkout: 'checkout', midstay: 'เปลี่ยนผ้าระหว่างพัก', manual: 'เพิ่มเอง' };
const sets = (n) => { const v = Number(n || 0); return Number.isInteger(v) ? String(v) : v.toFixed(1); };
const money = (n) => Number(n || 0).toLocaleString('th-TH');

const setUrlBatch = (id) => {
  try {
    const q = new URLSearchParams(window.location.search);
    if (id) q.set('batch', id); else q.delete('batch');
    window.history.replaceState(null, '', `${window.location.pathname}?${q.toString()}`);
  } catch (e) { /* ignore */ }
};

export default function LaundryWork({ me, cw, S }) {
  const initialBatch = new URLSearchParams(window.location.search).get('batch');
  const [batches, setBatches] = useState(null);
  const [detail, setDetail] = useState(null);   // รอบที่เปิดดูอยู่
  const [form, setForm] = useState(null);       // 'pickup' | 'return'
  const [actualSets, setActualSets] = useState('');
  const [note, setNote] = useState('');
  const [amount, setAmount] = useState('');
  const [receipt, setReceipt] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [busy, setBusy] = useState(false);

  const loadList = useCallback(() => {
    cw('laundry/batches').then((r) => setBatches(Array.isArray(r.data) ? r.data : [])).catch(() => setBatches([]));
  }, [cw]);

  const openBatch = useCallback((id) => {
    cw(`laundry/batch/${id}`).then((r) => {
      setDetail(r.data); setForm(null); setUrlBatch(id);
    }).catch(() => { setUrlBatch(null); });
  }, [cw]);

  useEffect(() => {
    loadList();
    if (initialBatch) openBatch(initialBatch);
    // eslint-disable-next-line
  }, []);

  const backToList = () => { setDetail(null); setForm(null); setUrlBatch(null); loadList(); };
  const fail = (e) => { setBusy(false); alert(e?.response?.data?.message || 'ไม่สำเร็จ — สัญญาณเน็ตอาจไม่ดี ลองอีกครั้ง'); };

  const acknowledge = (id) => {
    setBusy(true);
    cw(`laundry/batch/${id}/acknowledge`, { method: 'post' }).then(() => { setBusy(false); openBatch(id); }).catch(fail);
  };

  const startPickup = () => {
    setActualSets(String(Math.round(Number(detail.current_sets || 0)))); setNote(''); setForm('pickup');
  };
  const submitPickup = () => {
    const n = Number(actualSets);
    if (!(n > 0)) { alert('กรุณาใส่จำนวนชุดที่รับไป'); return; }
    if (!window.confirm(`ยืนยันรับผ้า ${n} ชุด?`)) return;
    setBusy(true);
    cw(`laundry/batch/${detail.id}/pickup`, { method: 'post', data: { actual_sets: n, note } })
      .then(() => { setBusy(false); openBatch(detail.id); }).catch(fail);
  };

  const startReturn = () => { setAmount(''); setNote(''); setReceipt(null); setForm('return'); };
  const onPickReceipt = async (file) => {
    if (!file) return;
    setUploading(true);
    try {
      const small = await shrinkImage(file);
      const fd = new FormData(); fd.append('file', small, 'receipt.jpg');
      const r = await cw('upload-photo', { method: 'post', data: fd, timeout: 90000 });
      setReceipt(r.data.url);
    } catch (e) { alert('ส่งรูปไม่สำเร็จ — ลองใหม่อีกครั้ง'); }
    setUploading(false);
  };
  const monthly = detail?.payment_status === 'void';
  const submitReturn = () => {
    if (uploading) { alert('รอรูปส่งเสร็จก่อนนะคะ'); return; }
    if (!monthly && !(amount !== '' && Number(amount) >= 0)) { alert('กรุณากรอกยอดเงิน'); return; }
    if (!window.confirm(monthly ? 'ยืนยันส่งคืนผ้าแล้ว?' : `ยืนยันส่งคืนผ้า + เบิก ฿${money(amount)}?`)) return;
    setBusy(true);
    cw(`laundry/batch/${detail.id}/return`, { method: 'post', data: { claim_amount: monthly ? (amount || null) : Number(amount), claim_note: note, receipt_photo_url: receipt } })
      .then(() => { setBusy(false); openBatch(detail.id); }).catch(fail);
  };

  const input = { boxSizing: 'border-box', width: '100%', border: '1.5px solid #CBD4CC', borderRadius: 10, padding: 12, fontSize: 17 };

  // ---------- DETAIL ----------
  if (detail) {
    const st = statusOf(detail);
    const items = detail.items || [];
    const isActive = ACTIVE.includes(detail.status);
    return (
      <div style={S.page}>
        <div style={S.bar}>
          <button style={S.back} onClick={backToList}>‹ รายการ</button>
          <div><h1 style={{ ...S.h, fontSize: 17 }}>รอบ #{detail.batch_no}</h1><div style={S.sub}>{detail.hotel?.name_th}</div></div>
        </div>

        <div style={{ ...S.card, background: '#F1F7F3' }}>
          <div style={{ display: 'flex', alignItems: 'center' }}>
            <div style={{ flex: 1, fontSize: 24, fontWeight: 800 }}>
              {isActive ? `~${sets(detail.current_sets)} ชุด` : `${sets(detail.actual_sets)} ชุด`}
            </div>
            <span style={S.pill(st.color)}>{st.label}</span>
          </div>
          {detail.first_dirty_at && isActive && <div style={{ ...S.sub, marginTop: 4 }}>ผ้าค้างตั้งแต่ {moment(detail.first_dirty_at).format('D MMM HH:mm')}</div>}
          {detail.picked_up_at && <div style={{ ...S.sub, marginTop: 4 }}>🧺 รับไป {moment(detail.picked_up_at).format('D MMM HH:mm')}{detail.pickup_note ? ` · ${detail.pickup_note}` : ''}</div>}
          {detail.returned_at && <div style={{ ...S.sub, marginTop: 2 }}>✅ ส่งคืน {moment(detail.returned_at).format('D MMM HH:mm')}</div>}
          {detail.claim_amount != null && <div style={{ fontSize: 15, fontWeight: 700, marginTop: 6 }}>ยอดเบิก ฿{money(detail.claim_amount)}</div>}
          {detail.status === 'paid' && <div style={{ fontSize: 15, fontWeight: 700, color: '#0F8A56', marginTop: 4 }}>💰 จ่ายแล้ว ฿{money(detail.paid_amount)}{detail.payment_note ? ` · ${detail.payment_note}` : ''}</div>}
          {detail.payment_slip_url && <a href={detail.payment_slip_url} target="_blank" rel="noreferrer"><img src={detail.payment_slip_url} alt="สลิป" style={{ marginTop: 8, height: 96, borderRadius: 8 }} /></a>}
        </div>

        {/* ปุ่มหลักตามสถานะ */}
        {!form && ['open', 'notified'].includes(detail.status) && (
          <button disabled={busy} style={{ ...S.bigBtn('#2C6DAF'), padding: 14, fontSize: 17 }} onClick={() => acknowledge(detail.id)}>รับทราบ จะไปรับผ้า</button>
        )}
        {!form && isActive && (
          <button disabled={busy} style={S.bigBtn('#0F8A56')} onClick={startPickup}>🧺 รับผ้าแล้ว</button>
        )}
        {!form && detail.status === 'picked_up' && (
          <button disabled={busy} style={S.bigBtn('#0F8A56')} onClick={startReturn}>✅ ส่งคืนผ้าแล้ว</button>
        )}

        {form === 'pickup' && (
          <div style={{ ...S.card, marginTop: 12 }}>
            <div style={{ fontWeight: 800, marginBottom: 4 }}>รับผ้าไปกี่ชุด?</div>
            <div style={{ ...S.sub, marginBottom: 10 }}>ระบบประมาณไว้ ~{sets(detail.current_sets)} ชุด — แก้ให้ตรงกับที่รับจริง (รวมผ้าเลอะอื่นๆ)</div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
              <button style={{ ...S.back, fontSize: 22, padding: '6px 18px' }} onClick={() => setActualSets(String(Math.max(0, Number(actualSets || 0) - 1)))}>−</button>
              <input type="number" inputMode="numeric" value={actualSets} onChange={(e) => setActualSets(e.target.value)} style={{ ...input, textAlign: 'center', fontSize: 24, fontWeight: 800 }} />
              <button style={{ ...S.back, fontSize: 22, padding: '6px 18px' }} onClick={() => setActualSets(String(Number(actualSets || 0) + 1))}>+</button>
            </div>
            <input value={note} onChange={(e) => setNote(e.target.value)} placeholder="โน้ต (ถ้ามี) เช่น มีผ้าม่าน 2 ผืน" style={{ ...input, fontSize: 15, marginTop: 10 }} />
            <button disabled={busy} style={S.bigBtn('#0F8A56')} onClick={submitPickup}>{busy ? 'กำลังส่ง…' : 'ยืนยันรับผ้า'}</button>
            <button style={{ ...S.back, width: '100%', marginTop: 8, color: '#586159' }} onClick={() => setForm(null)}>ยกเลิก</button>
          </div>
        )}

        {form === 'return' && (
          <div style={{ ...S.card, marginTop: 12 }}>
            <div style={{ fontWeight: 800, marginBottom: 10 }}>ส่งคืนผ้า {sets(detail.actual_sets)} ชุด</div>
            {!monthly && (
              <>
                <div style={{ ...S.sub, marginBottom: 4 }}>ยอดเงินที่เบิก (บาท)</div>
                <input type="number" inputMode="decimal" value={amount} onChange={(e) => setAmount(e.target.value)} placeholder="0" style={{ ...input, fontSize: 24, fontWeight: 800 }} />
              </>
            )}
            <input value={note} onChange={(e) => setNote(e.target.value)} placeholder="โน้ต (ถ้ามี) เช่น ผ้าขาด 1 ผืน คราบไม่ออก 2 ผืน" style={{ ...input, fontSize: 15, marginTop: 10 }} />
            <div style={{ marginTop: 10 }}>
              {receipt
                ? <div style={{ position: 'relative', display: 'inline-block' }}>
                    <img src={receipt} alt="บิล" style={{ height: 110, borderRadius: 10 }} />
                    <button onClick={() => setReceipt(null)} style={{ position: 'absolute', top: 4, right: 4, width: 30, height: 30, borderRadius: 15, border: 'none', background: 'rgba(0,0,0,.6)', color: '#fff' }}>✕</button>
                  </div>
                : <label style={{ display: 'block', border: '2px dashed #CBD4CC', borderRadius: 12, padding: 14, fontSize: 16, fontWeight: 800, textAlign: 'center' }}>
                    {uploading ? '⏳ กำลังส่งรูป…' : '📷 แนบรูปบิล (ถ้ามี)'}
                    <input type="file" accept="image/*" style={{ display: 'none' }} onChange={(e) => { onPickReceipt(e.target.files?.[0]); e.target.value = ''; }} />
                  </label>}
            </div>
            <button disabled={busy || uploading} style={S.bigBtn('#0F8A56')} onClick={submitReturn}>{busy ? 'กำลังส่ง…' : 'ยืนยันส่งคืน'}</button>
            <button style={{ ...S.back, width: '100%', marginTop: 8, color: '#586159' }} onClick={() => setForm(null)}>ยกเลิก</button>
          </div>
        )}

        {/* รายการผ้าในรอบ */}
        {items.length > 0 && (
          <div style={{ ...S.card, marginTop: 12 }}>
            <div style={{ fontWeight: 800, marginBottom: 6 }}>ที่มาของผ้าในรอบนี้</div>
            {items.map((i) => (
              <div key={i.id} style={{ display: 'flex', gap: 8, padding: '6px 0', borderTop: '1px solid #EEF1EE', fontSize: 14 }}>
                <div style={{ flex: 1 }}>
                  <div style={{ fontWeight: 700 }}>{i.room?.name_th || (i.source === 'manual' ? 'ผ้าเพิ่มเติม' : '-')}</div>
                  <div style={{ color: '#8A938B', fontSize: 12 }}>{SOURCE[i.source] || i.source} · {moment(i.event_at).format('D MMM HH:mm')}{i.note ? ` · ${i.note}` : ''}</div>
                </div>
                <div style={{ fontWeight: 800 }}>{sets(i.sets)}</div>
              </div>
            ))}
          </div>
        )}
      </div>
    );
  }

  // ---------- LIST ----------
  return (
    <div style={S.page}>
      <div style={{ marginBottom: 12 }}>
        <h1 style={S.h}>สวัสดีค่ะ {me.firstname} 🧺</h1>
        <div style={S.sub}>งานรับ-ส่งผ้า — แตะรอบเพื่อดู/กดรับผ้า</div>
      </div>
      {batches === null && <div style={{ ...S.card, textAlign: 'center', color: '#8A938B' }}>กำลังโหลด…</div>}
      {batches && batches.length === 0 && <div style={{ ...S.card, textAlign: 'center', color: '#8A938B' }}>ยังไม่มีงาน — ระบบจะแจ้งในไลน์เมื่อถึงเวลามารับผ้า</div>}
      {(batches || []).map((b) => {
        const st = statusOf(b);
        const isActive = ACTIVE.includes(b.status);
        return (
          <button key={b.id} onClick={() => openBatch(b.id)}
            style={{ ...S.card, display: 'block', width: '100%', textAlign: 'left', borderColor: ['notified', 'picked_up'].includes(b.status) ? st.color : '#DBE2DC' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <div style={{ flex: 1 }}>
                <div style={{ fontSize: 17, fontWeight: 800 }}>รอบ #{b.batch_no} · {isActive ? `~${sets(b.current_sets)}` : sets(b.actual_sets)} ชุด</div>
                <div style={{ fontSize: 12, color: '#8A938B', marginTop: 2 }}>
                  {b.hotel?.name_th}
                  {b.picked_up_at ? ` · รับ ${moment(b.picked_up_at).format('D MMM')}` : (b.first_dirty_at ? ` · ค้างตั้งแต่ ${moment(b.first_dirty_at).format('D MMM')}` : '')}
                </div>
                {b.claim_amount != null && <div style={{ fontSize: 13, fontWeight: 700, marginTop: 2 }}>฿{money(b.status === 'paid' ? b.paid_amount : b.claim_amount)}</div>}
              </div>
              <span style={S.pill(st.color)}>{st.label}</span>
            </div>
          </button>
        );
      })}
    </div>
  );
}
